import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OverrideProperties } from 'type-fest';

import {
  Select,
  CreatableSelect,
  Box,
  SelectComponents,
  type SelectProps,
} from '@frontend/components';

import { useDebounce } from 'utils/hooks';

import { contactSearch, fetchContact, paginateContacts } from './data/actions';
import { UNASSIGNED_VALUE } from './data/constants';
import {
  getCachedContacts,
  getIsSearching,
  getSearchResults,
  getResultCount,
} from './data/reducer';

const INPUT_CHANGE = 'input-change';
const CREATE_MODE = 'create';

const OptionLabel = ({ data: { value, label, contact } }) => {
  return (
    <>
      {value === UNASSIGNED_VALUE ? (
        label
      ) : !contact ? (
        value
      ) : (
        <>
          {contact.data.fullName && (
            <Box as="strong" width="100%" fontWeight="heavy">
              {contact.data.fullName}&nbsp;
            </Box>
          )}
          {contact.data.accounts?.[0]?.name ? (
            <Box as="span" color="feature.secondary">
              {contact.innerHits?.accounts?.[0]?.name
                ? contact.innerHits.accounts[0].name
                : contact.data.accounts[0].name}
              &nbsp;|&nbsp;
            </Box>
          ) : null}
          {contact.data.email && (
            <Box as="span" color="feature.secondary">
              {contact.data.email}
            </Box>
          )}
        </>
      )}
    </>
  );
};

const CustomOption = ({ innerProps, children, data, ...restProps }) => (
  <SelectComponents.Option
    {...restProps}
    data={data}
    innerProps={{ ...innerProps, flexWrap: 'wrap' }}
  >
    {children}
    {data.isSuggestion ? (
      <Box as="span" width="100%" color="interaction.suggestion">
        {data.contact.data.sourceName}
      </Box>
    ) : null}
  </SelectComponents.Option>
);

export type ContactSelectorProps = OverrideProperties<
  SelectProps,
  {
    addEmail?: (email: string) => void;
    allowMultiSelect?: boolean;
    allowUnassignedOption?: boolean;
    allowMixedUnassignedOptions?: boolean;
    onChange: (selections: string | null) => void;
    searchFields?: string[];
    searchFilters?: any;
    selectedValues?: (number | string)[];
    textFeedback?: (text: string) => void;
    mode?: string;
    menuPortalDestinationRef?: any;
    suggestions?: {
      suggestionId: string;
      contactId?: number;
      email?: string;
      fullName?: string;
      accounts?: [{ name?: string }];
    }[];
  }
>;

export const ContactSelector = forwardRef<'div', ContactSelectorProps>(
  (
    {
      addEmail,
      allowMultiSelect = false,
      allowUnassignedOption = false,
      allowMixedUnassignedOptions = false,
      menuPortalDestinationRef,
      onChange,
      searchFields = [
        'email',
        'full_name',
        'phone',
        'accounts.erp_code',
        'accounts.name',
      ],
      searchFilters,
      selectedValues = [],
      textFeedback,
      suggestions,
      mode,
      components,
      ...restProps
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const fetchContactRequest = useCallback(
      ({ contactId }) => dispatch(fetchContact.request({ contactId })),
      [dispatch],
    );
    const contactSearchRequest = useCallback(
      ({ search, searchFields, searchFilters, paginateFrom }) =>
        dispatch(
          contactSearch.request({
            search,
            searchFields,
            searchFilters,
            paginateFrom,
          }),
        ),
      [dispatch],
    );
    const paginateContactsRequest = useCallback(
      ({ searchFields, searchFilters, paginateFrom }) =>
        dispatch(
          paginateContacts.request({
            searchFields,
            searchFilters,
            paginateFrom,
          }),
        ),
      [dispatch],
    );

    const resultCount = useSelector(getResultCount);
    const cachedContacts = useSelector(getCachedContacts);
    const isSearching = useSelector(getIsSearching);
    const searchResults = useSelector(getSearchResults);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOptions, setSelectedOptions] = useState([]);
    const debouncedSearchTerm = useDebounce(searchTerm, 250);
    const debounceSearchCount = useRef(0);
    const fieldsRef = useRef(searchFields);
    const filtersRef = useRef(searchFilters);
    const contactsRequested = useRef<Record<number, true>>({});
    const [paginateFrom, setPaginateFrom] = useState(0);

    /*
  If there are selected values that have not been found from our current search,
  we need to fetch them and map them to options.
  */
    const values = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];
    const missingSelectedOptions = values.filter(
      (value) =>
        !selectedOptions.some(
          (selectedOption) => selectedOption.value === value,
        ),
    );
    if (missingSelectedOptions.length) {
      // there are selected options that have not been found via search
      const optionsFromCache = [];
      missingSelectedOptions.forEach((missingOption) => {
        const cachedContact = cachedContacts.find(
          (contact) => contact.id === missingOption,
        );
        // map the cached contact to resemble a search result
        if (cachedContact) {
          const fullName = `${cachedContact.firstName} ${cachedContact.lastName}`;
          optionsFromCache.push({
            value: cachedContact.id,
            label: fullName,
            contact: {
              data: {
                ...cachedContact,
                fullName: fullName,
              },
            },
          });
        } else {
          // fetch the missing contact from the backend
          if (!contactsRequested.current[missingOption]) {
            fetchContactRequest({ contactId: missingOption });
            contactsRequested.current[missingOption] = true;
          }
        }

        if (optionsFromCache.length) {
          setSelectedOptions([...selectedOptions, ...optionsFromCache]);
        }
      });
    }

    let options = [
      ...selectedOptions,
      ...searchResults.flatMap((contact) => {
        const value = contact?.data?.id;
        return value == null
          ? []
          : [
              {
                contact,
                value,
                label: contact.data?.fullName,
              },
            ];
      }),
    ];

    if (allowUnassignedOption) {
      options.unshift({
        contact: { data: { id: UNASSIGNED_VALUE } },
        value: UNASSIGNED_VALUE,
        label: 'Unassigned',
      });
    }

    options = options.filter((option, index) => {
      //filter options with undefined or null
      if (option.value == null) return false;
      // keep unassigned value
      else if (option.value === UNASSIGNED_VALUE) return true;
      //filter duplicate options by `value`
      else {
        return (
          options.findIndex(
            (opt) => Number(opt.value) === Number(option.value),
          ) === index
        );
      }
    });

    const optionsLength = options.length;

    if (suggestions && suggestions.length > 0) {
      const createSuggestions = [];
      const selectSuggestions = [];
      for (const suggestion of suggestions) {
        if (suggestion.contactId) {
          selectSuggestions.push(suggestion);
        } else {
          createSuggestions.push(suggestion);
        }
      }
      const createOptions = {
        label: 'Create contact from suggestion',
        options: createSuggestions.map((suggestion) => ({
          isSuggestion: true,
          ...suggestion,
          value: suggestion.contactId ?? suggestion.suggestionId,
          label: suggestion.fullName,
          shouldCreate: !suggestion.contactId,
          contact: { data: suggestion },
        })),
      };
      const selectOptions = {
        label: 'Select contact from suggestion',
        options: selectSuggestions.map((suggestion) => ({
          isSuggestion: true,
          ...suggestion,
          value: suggestion.contactId ?? suggestion.suggestionId,
          label: suggestion.fullName,
          shouldCreate: !suggestion.contactId,
          contact: { data: suggestion },
        })),
      };
      const standardOptions = {
        options: options.filter(({ value }) =>
          suggestions.every((s) => s.contactId !== value),
        ),
      };

      options = [selectOptions, createOptions, standardOptions];
    }

    const flatOptions = options.flatMap((option) => option.options ?? option);
    // initiate on load
    useEffect(() => {
      contactSearchRequest({
        search: '',
        searchFields: fieldsRef.current,
        searchFilters: filtersRef.current,
        paginateFrom: 0,
      });
    }, [contactSearchRequest]);

    useEffect(() => {
      // do not send update on initial load
      if (debounceSearchCount.current > 0) {
        contactSearchRequest({
          search: debouncedSearchTerm,
          searchFields: fieldsRef.current,
          searchFilters: filtersRef.current,
          paginateFrom: 0,
        });
        setPaginateFrom(0);
      }
      debounceSearchCount.current++;
    }, [debouncedSearchTerm, contactSearchRequest]);

    useEffect(() => {
      if (paginateFrom > 0) {
        paginateContactsRequest({
          searchFields: fieldsRef.current,
          searchFilters: filtersRef.current,
          paginateFrom,
        });
      }
    }, [paginateContactsRequest, paginateFrom]);

    function convertToValues(selections: any) {
      // {contact:{}, label: string, value: number}
      if (selections.value != null) {
        return [selections.value];
      }

      // [{contact:{}, label: string, value: number}]
      if (Array.isArray(selections)) {
        return selections.map((selection) => selection.value ?? selection);
      }
    }

    const handleChange = (selections) => {
      if (!selections) {
        onChange(null);
        return;
      }

      if (mode === CREATE_MODE) {
        const { email } = selections?.contact?.data ?? {};
        onChange(email);
        return;
      }

      let nextSelections = convertToValues(selections);
      // handle unassigned option
      if (allowUnassignedOption && !allowMixedUnassignedOptions) {
        if ((selectedValues || []).includes(UNASSIGNED_VALUE)) {
          // if you choose a value and you previously had unassigned selected, we remove the unassigned value
          nextSelections = selections
            .filter((selection) => selection.value !== UNASSIGNED_VALUE)
            .map((s) => s.value);
        } else if (
          selections.length > 1 &&
          selections
            .slice(1)
            .filter((selection) => selection.value === UNASSIGNED_VALUE)
            .length > 0
        ) {
          // if you choose the unassigned value, no other value can be selected
          nextSelections = [UNASSIGNED_VALUE];
        }
      }
      const selectedOptions = nextSelections.map((selection) =>
        flatOptions.find((option) => option.value === selection),
      );

      // store selected options incase they are not available after a search query
      setSelectedOptions(
        selectedOptions.filter(({ isSuggestion }) => !isSuggestion),
      );

      // pass back selected contacts
      onChange(
        selectedOptions.map((option) => ({
          shouldCreate: option.shouldCreate,
          ...option.contact.data,
        })),
      );
      setSearchTerm('');
      if (textFeedback) {
        textFeedback('');
      }
    };

    const handleTextChange = (text, { action }) => {
      if (action === INPUT_CHANGE) {
        setSearchTerm(text);
        setPaginateFrom(0);
        if (textFeedback) {
          textFeedback(text);
        }
      }
    };

    const handleBlur = () => {
      setSearchTerm('');
      setPaginateFrom(0);
    };

    const handleOnScrollBottom = () => {
      if (optionsLength < resultCount) {
        setPaginateFrom(paginateFrom + 10);
      }
    };

    const selectValue = selectedValues?.map((val) =>
      flatOptions.find((opt) => {
        if (opt.value === val) {
          return {
            label: opt.label,
            value: opt.value,
          };
        }
      }),
    );

    const commonProps = {
      closeMenuOnSelect: !allowMultiSelect,
      filterOption: () => true,
      inputValue: searchTerm,
      isClearable: false,
      isLoading: isSearching,
      isMulti: allowMultiSelect,
      placeholder: allowMultiSelect
        ? 'Select one or more contacts'
        : 'Select a contact',
      menuPortalDestinationRef,
      onChange: handleChange,
      onInputChange: handleTextChange,
      onMenuScrollToBottom: handleOnScrollBottom,
      onBlur: handleBlur,
      options,
      value: selectValue,
      formatOptionLabel: (data) => <OptionLabel data={data} />,
      isSuggestionsEnabled: suggestions && suggestions.length > 0,
      components: {
        Option: CustomOption,
        ...components,
      },
      ...restProps,
    };

    if (mode !== CREATE_MODE) {
      return <Select ref={ref} {...commonProps} />;
    } else {
      return (
        <CreatableSelect
          ref={ref}
          {...commonProps}
          placeholder={`${commonProps.placeholder} or enter an email`}
          onCreateOption={addEmail}
          createOptionPosition="first"
          formatCreateLabel={(value) => `Use "${value}"`}
        />
      );
    }
  },
);

ContactSelector.displayName = 'ContactSelector';

export default ContactSelector;
